/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import axios from "axios";
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";

export type NotificationType = "activity" | "campaign" | "profile" | "system";

export interface Notification {
  _id: string;
  heading: string;
  subheading: string;
  type: "campaign" | "activity" | "profile" | "collaboration" | "system";
  read: boolean;
  readOn?: Date;
  status: "active" | "archived" | "deleted";
  redirectionLink?: string;
  externalLink?: string;
  influencerId?: string;
  brandId?: string;
  mentionedInfluencerId?: string;
  mentionedBrandId?: string;
  collabId?: string;
  createdAt?: Date;
  campaign_ID?: string;
  metadata: {
    readBy: string[];
  };
  cta?: string;
}

// Standardized response interface for all API calls
interface ApiResponse<T> {
  data: T | null;
  error: string | null;
}

// Standardized notification data response
interface NotificationData {
  notifications: Notification[];
  total: number;
  page: number;
  pages: number;
}

interface NotificationContextProps {
  // States
  notifications: Notification[];
  unreadNotifications: Notification[];
  readNotifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  currentMainPage: number;
  AllMainPage: number;
  currentReadPage: number;
  AllReadPage: number;
  currentUnreadPage: number;
  AllUnreadPage: number;

  // Actions
  fetchNotifications: () => Promise<ApiResponse<{ result: NotificationData }>>;
  fetchUnreadNotifications: () => Promise<
    ApiResponse<{ result: NotificationData }>
  >;
  fetchReadNotifications: () => Promise<
    ApiResponse<{ result: NotificationData }>
  >;
  fetchUnreadCount: () => Promise<ApiResponse<{ count: number }>>;
  markAsRead: (notificationId: string) => Promise<ApiResponse<null>>;
  markAllAsRead: () => Promise<ApiResponse<null>>;
  deleteNotification: (notificationId: string) => Promise<ApiResponse<null>>;
  refreshNotifications: () => Promise<{
    all: ApiResponse<{ result: NotificationData }>;
    unread: ApiResponse<{ result: NotificationData }>;
    read: ApiResponse<{ result: NotificationData }>;
    count: ApiResponse<{ count: number }>;
  }>;
  fetchNextPageNotifications: (
    type: "all" | "read" | "unread"
  ) => Promise<ApiResponse<{ result: NotificationData }>>;
}

interface NotificationProviderProps {
  children: React.ReactNode;
}

const NotificationContext = createContext<NotificationContextProps>(
  {} as NotificationContextProps
);

// eslint-disable-next-line react-refresh/only-export-components
export const useNotification = () => {
  return useContext(NotificationContext);
};

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  // States
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadNotifications, setUnreadNotifications] = useState<
    Notification[]
  >([]);
  const [readNotifications, setReadNotifications] = useState<Notification[]>(
    []
  );
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [currentMainPage, setCurrentMainPage] = useState<number>(1);
  const [AllMainPage, setAllMainPage] = useState<number>(1);
  const [currentReadPage, setCurrentReadPage] = useState<number>(1);
  const [AllReadPage, setAllReadPage] = useState<number>(1);
  const [currentUnreadPage, setCurrentUnreadPage] = useState<number>(1);
  const [AllUnreadPage, setAllUnreadPage] = useState<number>(1);

  const API_URL = import.meta.env.VITE_BACKEND_SERVER_URL;
  // Helper function to create API request headers
  const getHeaders = () => ({
    headers: {
      jwtToken: localStorage.getItem("jwtToken"),
    },
  });

  // Generic API request handler for consistent error handling
  const apiRequest = async <T,>(
    requestFn: () => Promise<any>
  ): Promise<ApiResponse<T>> => {
    setLoading(true);
    setError(null);
    try {
      const response = await requestFn();
      return { data: response.data, error: null };
    } catch (e: any) {
      const errorMessage =
        e.response?.data?.error || "An unknown error occurred";
      setError(errorMessage);
      return { data: null, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      window.location.href?.toLowerCase()?.includes("influencer") ||
      window.location.href?.toLowerCase()?.includes("brand")
    ) {
      refreshNotifications();
    }
  }, []);

  // Fetch all notifications for a user
  const fetchNotifications = useCallback(async () => {
    const response = await apiRequest<{ result: NotificationData }>(() =>
      axios.get(`${API_URL}/notification/user`, getHeaders())
    );

    if (response.data) {
      setNotifications(response.data.result.notifications);
      setCurrentMainPage(response.data.result.page);
      setAllMainPage(response.data.result.pages);
    }

    return response;
  }, [API_URL]);

  // Fetch unread notifications for a user
  const fetchUnreadNotifications = useCallback(async () => {
    const response = await apiRequest<{ result: NotificationData }>(() =>
      axios.get(`${API_URL}/notification/user/unread`, getHeaders())
    );

    if (response.data) {
      setUnreadNotifications(response.data.result.notifications);
      setCurrentUnreadPage(response.data.result.page);
      setAllUnreadPage(response.data.result.pages);
    }

    return response;
  }, [API_URL]);

  // Fetch read notifications for a user
  const fetchReadNotifications = useCallback(async () => {
    const response = await apiRequest<{ result: NotificationData }>(() =>
      axios.get(`${API_URL}/notification/user/read`, getHeaders())
    );

    if (response.data) {
      setReadNotifications(response.data.result.notifications);
      setCurrentReadPage(response.data.result.page);
      setAllReadPage(response.data.result.pages);
    }

    return response;
  }, [API_URL]);

  // Fetch unread notifications count
  const fetchUnreadCount = useCallback(async () => {
    const response = await apiRequest<{ count: number }>(() =>
      axios.get(`${API_URL}/notification/user/unread-count`, getHeaders())
    );

    if (response.data) {
      setUnreadCount(response.data.count);
    }

    return response;
  }, [API_URL]);

  // Mark a notification as read
  const markAsRead = useCallback(
    async (notificationId: string) => {
      const response = await apiRequest<null>(() =>
        axios.patch(
          `${API_URL}/notification/${notificationId}/read`,
          {},
          getHeaders()
        )
      );

      if (!response.error) {
        // Update local state
        setNotifications((prev) =>
          prev.map((notification) =>
            notification._id === notificationId
              ? { ...notification, read: true }
              : notification
          )
        );

        // Move notification from unread to read
        const targetNotification = unreadNotifications.find(
          (n) => n._id === notificationId
        );

        if (targetNotification) {
          const updatedNotification = { ...targetNotification, read: true };
          setUnreadNotifications((prev) =>
            prev.filter((n) => n._id !== notificationId)
          );
          setReadNotifications((prev) => [...prev, updatedNotification]);
          setUnreadCount((prev) => Math.max(0, prev - 1));
        }
      }

      return response;
    },
    [API_URL, unreadNotifications]
  );

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    const response = await apiRequest<null>(() =>
      axios.patch(
        `${API_URL}/notification/user/mark-all-read`,
        {},
        getHeaders()
      )
    );

    if (!response.error) {
      setNotifications((prev) =>
        prev.map((notification) => ({ ...notification, read: true }))
      );

      const newReadNotifications = unreadNotifications.map((n) => ({
        ...n,
        read: true,
      }));

      setReadNotifications((prev) => [...prev, ...newReadNotifications]);
      setUnreadNotifications([]);
      setUnreadCount(0);
    }

    return response;
  }, [API_URL, unreadNotifications]);

  // Delete a notification
  const deleteNotification = useCallback(
    async (notificationId: string) => {
      const response = await apiRequest<null>(() =>
        axios.delete(`${API_URL}/notification/${notificationId}`, getHeaders())
      );

      if (!response.error) {
        setNotifications((prev) =>
          prev.filter((n) => n._id !== notificationId)
        );
        setUnreadNotifications((prev) =>
          prev.filter((n) => n._id !== notificationId)
        );
        setReadNotifications((prev) =>
          prev.filter((n) => n._id !== notificationId)
        );

        const wasUnread = unreadNotifications.some(
          (n) => n._id === notificationId
        );

        if (wasUnread) {
          setUnreadCount((prev) => Math.max(0, prev - 1));
        }
      }

      return response;
    },
    [API_URL, unreadNotifications]
  );

  // Refresh all notification data for a user
  const refreshNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const all = await fetchNotifications();
      const unread = await fetchUnreadNotifications();
      const read = await fetchReadNotifications();
      const count = await fetchUnreadCount();

      return { all, unread, read, count };
    } catch (e: any) {
      const errorMessage =
        e.response?.data?.error || "An unknown error occurred";
      setError(errorMessage);

      return {
        all: { data: null, error: errorMessage },
        unread: { data: null, error: errorMessage },
        read: { data: null, error: errorMessage },
        count: { data: null, error: errorMessage },
      };
    } finally {
      setLoading(false);
    }
  }, [
    fetchNotifications,
    fetchUnreadNotifications,
    fetchReadNotifications,
    fetchUnreadCount,
  ]);

  const fetchNextPageNotifications = useCallback(
    async (type: "all" | "read" | "unread") => {
      const response = await apiRequest<{ result: NotificationData }>(() =>
        type === "all"
          ? axios.get(
              `${API_URL}/notification/user?page=${currentMainPage + 1}`,
              getHeaders()
            )
          : type === "read"
          ? axios.get(
              `${API_URL}/notification/user/read?page=${currentReadPage + 1}`,
              getHeaders()
            )
          : axios.get(
              `${API_URL}/notification/user/unread?page=${
                currentUnreadPage + 1
              }`,
              getHeaders()
            )
      );

      if (response.data) {
        if (type === "all") {
          setCurrentMainPage(currentMainPage + 1);
          setNotifications((prev) => [
            ...prev,
            ...response.data.result.notifications,
          ]);
        } else if (type === "read") {
          setCurrentReadPage(currentReadPage + 1);
          setReadNotifications((prev) => [
            ...prev,
            ...response.data.result.notifications,
          ]);
        } else if (type === "unread") {
          setCurrentUnreadPage(currentUnreadPage + 1);
          setUnreadNotifications((prev) => [
            ...prev,
            ...response.data.result.notifications,
          ]);
        }
      }

      return response;
    },
    [API_URL]
  );

  const contextValue: NotificationContextProps = {
    // States
    notifications,
    unreadNotifications,
    readNotifications,
    unreadCount,
    loading,
    error,
    currentMainPage,
    currentReadPage,
    currentUnreadPage,
    AllMainPage,
    AllReadPage,
    AllUnreadPage,

    // Actions
    fetchNotifications,
    fetchUnreadNotifications,
    fetchReadNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refreshNotifications,
    fetchNextPageNotifications,
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};
